import {
  CHECK_KINDS,
  type CheckKind,
  seesMoney,
  TOOL_EVENT_KINDS,
  TOOL_STATES,
  type ToolRepair,
  type ToolState,
  type ToolUnit,
  type ToolUnitInput,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { QrCode } from 'lucide-react';
import { useState } from 'react';
import { HistoryButton } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, tenge, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { PreservationPanel, UnitFilesPanel } from './PreservationPanel';
import {
  CHECK_TONE,
  itemsQuery,
  parseNum,
  peopleQuery,
  TOOL_STATE_TONE,
  toolQuery,
  warehousesQuery,
} from './queries';

const RESOURCE_LABEL: Record<ToolUnit['resourceState'], string> = {
  none: '',
  ok: 'ресурс в норме',
  near: 'ресурс почти выработан',
  exhausted: 'ресурс выработан — выдача закрыта до продления',
};
const RESOURCE_TONE: Record<ToolUnit['resourceState'], 'neutral' | 'success' | 'warning' | 'danger'> = {
  none: 'neutral',
  ok: 'success',
  near: 'warning',
  exhausted: 'danger',
};

const CHECK_LABEL: Record<ToolUnit['checkState'], string> = {
  none: 'без проверок',
  ok: 'проверка в срок',
  soon: 'проверка скоро',
  overdue: 'срок проверки прошёл — выдача закрыта',
};

/** Карточка экземпляра инструмента: где и у кого, проверки, история (СК-28…30, 33) */
export function ToolPage() {
  const { unitId } = useParams({ strict: false }) as { unitId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery(toolQuery(unitId));
  const u = q.data;
  useCrumbs(
    u
      ? [
          { label: 'Инструмент', to: '/warehouse?tab=tools' },
          { label: u.inventoryNumber, title: u.itemName },
        ]
      : null,
    q.error,
  );
  const [dialog, setDialog] = useState<
    '' | 'issue' | 'check' | 'state' | 'edit' | 'return' | 'repair' | 'repair_close' | 'extend'
  >('');
  const repairs = useQuery({
    queryKey: ['wh', 'tool-repairs', unitId],
    queryFn: () => api<ToolRepair[]>(`/warehouse/tools/${unitId}/repairs`),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['wh'] });
  };
  const confirm = useMutation({
    mutationFn: () => api<ToolUnit>(`/warehouse/tools/${unitId}/confirm`, { method: 'POST' }),
    onSuccess: () => {
      refresh();
      toast('Получение подтверждено — инструмент числится за вами');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError || !u)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const where = u.counterpartyName
    ? `у подрядчика ${u.counterpartyName}${u.actNumber ? ` по акту ${u.actNumber}` : ''}`
    : u.holderName
      ? `у ${u.holderName} с ${date(u.holderSince)}`
      : u.pendingHolderName
        ? `выдан ${u.pendingHolderName}, ждёт подтверждения`
        : `на складе ${u.warehouseName}`;
  return (
    <Page>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {u.inventoryNumber}
            <Badge tone={TOOL_STATE_TONE[u.state]}>{TOOL_STATES[u.state]}</Badge>
            {u.checkKind !== 'none' ? (
              <Badge tone={CHECK_TONE[u.checkState]}>{CHECK_LABEL[u.checkState]}</Badge>
            ) : null}
            {u.returnOverdue ? <Badge tone="danger">возврат просрочен</Badge> : null}
            {u.preservationState === 'overdue' ? <Badge tone="danger">консервация просрочена</Badge> : null}
            {u.preservationState === 'soon' ? <Badge tone="warning">консервация скоро</Badge> : null}
          </span>
        }
        subtitle={
          <>
            <Link to={`/warehouse/items/${u.itemId}`} className="hover:underline">
              {u.itemName}
            </Link>
            {u.model ? ` · ${u.model}` : ''} · {where}
          </>
        }
        actions={
          <>
            {u.can.confirm ? (
              <Button variant="primary" loading={confirm.isPending} onClick={() => confirm.mutate()}>
                Подтвердить получение
              </Button>
            ) : null}
            {u.can.issue ? (
              <Button variant="primary" onClick={() => setDialog('issue')}>
                Выдать
              </Button>
            ) : null}
            {u.can.return ? <Button onClick={() => setDialog('return')}>Вернуть на склад</Button> : null}
            {u.can.edit && u.checkKind !== 'none' ? (
              <Button onClick={() => setDialog('check')}>Проверка пройдена</Button>
            ) : null}
            {u.can.edit && u.state === 'repair' ? (
              <Button variant="primary" onClick={() => setDialog('repair_close')}>
                Закрыть ремонт
              </Button>
            ) : null}
            {u.can.edit &&
            u.state === 'ok' &&
            !u.holderUserId &&
            !u.pendingHolderUserId &&
            !u.counterpartyId ? (
              <Button onClick={() => setDialog('repair')}>В ремонт</Button>
            ) : null}
            {u.can.extend && u.resourceKind ? (
              <Button onClick={() => setDialog('extend')}>Продлить ресурс</Button>
            ) : null}
            {u.can.edit ? <Button onClick={() => setDialog('state')}>Состояние</Button> : null}
            {u.can.edit ? <Button onClick={() => setDialog('edit')}>Править</Button> : null}
            <Button
              icon={<QrCode className="size-4" />}
              onClick={() => navigate({ to: '/warehouse/labels', search: { units: u.id } })}
            >
              Этикетка
            </Button>
            <HistoryButton table="tool_units" rowId={u.id} title={u.inventoryNumber} />
          </>
        }
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <Panel title="Карточка">
          <KeyValue
            items={[
              { label: 'Позиция каталога', value: `${u.itemCode} · ${u.itemName}` },
              { label: 'Серийный номер', value: u.serialNumber ?? '—' },
              { label: 'Модель', value: u.model ?? '—' },
              {
                label: 'Склад',
                value: (
                  <Link className="hover:underline" to={`/warehouse/warehouses/${u.warehouseId}`}>
                    {u.warehouseName}
                  </Link>
                ),
              },
              { label: 'Где сейчас', value: where },
              ...(u.returnBy ? [{ label: 'Вернуть до', value: date(u.returnBy) }] : []),
              ...(u.kitRef ? [{ label: 'Комплект', value: u.kitRef }] : []),
              {
                label: 'Состояние',
                value: `${TOOL_STATES[u.state]}${u.condition ? ` — ${u.condition}` : ''}`,
              },
              {
                label: 'Проверки',
                value:
                  u.checkKind === 'none'
                    ? 'не требуются'
                    : `${CHECK_KINDS[u.checkKind]}, раз в ${u.checkIntervalDays} дн.`,
              },
              ...(u.checkKind !== 'none'
                ? [
                    {
                      label: 'Последняя проверка',
                      value: u.lastCheckOn
                        ? `${date(u.lastCheckOn)}${u.checkDocument ? `, ${u.checkDocument}` : ''}`
                        : 'не было',
                    },
                    { label: 'Следующая', value: u.nextCheckOn ? date(u.nextCheckOn) : '—' },
                  ]
                : []),
              ...(u.dailyRate !== null ? [{ label: 'Ставка за день', value: tenge(u.dailyRate) }] : []),
              ...(u.resourceKind
                ? [
                    {
                      label: 'Ресурс',
                      value: (
                        <span>
                          {u.resourceUsed} из {u.resourceLimit ?? '—'}{' '}
                          {u.resourceKind === 'hours' ? 'моточасов' : 'дней на руках'}{' '}
                          <Badge tone={RESOURCE_TONE[u.resourceState]}>
                            {RESOURCE_LABEL[u.resourceState]}
                          </Badge>
                          {u.resourceExtendNote ? (
                            <span className="block text-[12px] text-ink-2">
                              продлён: {u.resourceExtendNote}
                            </span>
                          ) : null}
                        </span>
                      ),
                    },
                  ]
                : []),
              ...(u.repairDueOn
                ? [
                    {
                      label: 'В ремонте',
                      value: `срок ${date(u.repairDueOn)}${u.repairOverdue ? ' — срок прошёл' : ''}`,
                    },
                  ]
                : []),
              ...(u.note ? [{ label: 'Примечание', value: u.note }] : []),
            ]}
          />
        </Panel>
        <PreservationPanel u={u} />
        <UnitFilesPanel u={u} />
        <Panel title="Ремонты" meta={repairs.data?.length ? String(repairs.data.length) : undefined}>
          {repairs.data?.length ? (
            <ul className="flex flex-col gap-2 text-[14px]">
              {repairs.data.map((r) => (
                <li key={r.id}>
                  <span className="font-medium">
                    {date(r.sentOn)} → {r.returnedOn ? date(r.returnedOn) : `срок ${date(r.dueOn)}`}
                  </span>
                  {' · '}
                  <Badge
                    tone={
                      r.status === 'open'
                        ? r.overdue
                          ? 'danger'
                          : 'warning'
                        : r.status === 'done'
                          ? 'success'
                          : 'neutral'
                    }
                  >
                    {r.status === 'open'
                      ? r.overdue
                        ? 'в ремонте, срок прошёл'
                        : 'в ремонте'
                      : r.status === 'done'
                        ? 'исправен'
                        : 'списан'}
                  </Badge>
                  <div className="text-[13px] text-ink-2">
                    {r.counterpartyName ?? 'своими силами'} · {r.reason}
                    {r.result ? ` → ${r.result}` : ''}
                    {r.cost !== null ? ` · затраты ${tenge(r.cost)}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-ink-2">Ремонтов не было.</p>
          )}
        </Panel>
        <Panel title="История" bodyClassName="p-0">
          <ol className="divide-y divide-line">
            {u.events.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 px-5 py-2.5 text-[14px]"
              >
                <div>
                  <span className="font-medium">{TOOL_EVENT_KINDS[e.kind]}</span>
                  {e.userName ? <span> — {e.userName}</span> : null}
                  {e.counterpartyName ? <span> — {e.counterpartyName}</span> : null}
                  {e.returnBy ? <span className="text-ink-2"> · вернуть до {date(e.returnBy)}</span> : null}
                  {e.note ? <div className="text-[13px] text-ink-2">{e.note}</div> : null}
                </div>
                <span className="num text-[12px] text-ink-2">
                  {dateTime(e.at)}
                  {e.byName ? ` · ${e.byName}` : ''}
                </span>
              </li>
            ))}
          </ol>
        </Panel>
      </div>
      {dialog === 'issue' ? <IssueDialog u={u} onClose={() => setDialog('')} onDone={refresh} /> : null}
      {dialog === 'return' ? <ReturnDialog u={u} onClose={() => setDialog('')} onDone={refresh} /> : null}
      {dialog === 'check' ? <CheckDialog u={u} onClose={() => setDialog('')} onDone={refresh} /> : null}
      {dialog === 'state' ? <StateDialog u={u} onClose={() => setDialog('')} onDone={refresh} /> : null}
      {dialog === 'repair' ? <RepairDialog u={u} onClose={() => setDialog('')} onDone={refresh} /> : null}
      {dialog === 'repair_close' ? (
        <RepairCloseDialog u={u} onClose={() => setDialog('')} onDone={refresh} />
      ) : null}
      {dialog === 'extend' ? <ExtendDialog u={u} onClose={() => setDialog('')} onDone={refresh} /> : null}
      {dialog === 'edit' ? (
        <Dialog
          open
          onOpenChange={(v) => !v && setDialog('')}
          title={`Экземпляр ${u.inventoryNumber}`}
          width="lg"
        >
          <ToolUnitForm
            initial={u}
            onCancel={() => setDialog('')}
            onSaved={() => {
              refresh();
              setDialog('');
            }}
          />
        </Dialog>
      ) : null}
    </Page>
  );
}

function IssueDialog({ u, onClose, onDone }: { u: ToolUnit; onClose: () => void; onDone: () => void }) {
  const people = useQuery(peopleQuery);
  const contractors = useQuery(counterpartiesQuery('contractor'));
  const projects = useQuery(projectsQuery());
  const [to, setTo] = useState<'person' | 'contractor'>('person');
  const [f, setF] = useState({
    userId: '',
    counterpartyId: '',
    actNumber: '',
    returnBy: '',
    projectId: '',
    kitRef: '',
    note: '',
  });
  const m = useMutation({
    mutationFn: () =>
      api<ToolUnit>(`/warehouse/tools/${u.id}/issue`, {
        body: {
          userId: to === 'person' ? f.userId || null : null,
          counterpartyId: to === 'contractor' ? f.counterpartyId || null : null,
          actNumber: to === 'contractor' ? f.actNumber.trim() || null : null,
          returnBy: f.returnBy,
          projectId: f.projectId || null,
          kitRef: f.kitRef.trim() || null,
          note: f.note.trim() || null,
        },
      }),
    onSuccess: () => {
      onDone();
      toast(
        to === 'person' ? 'Выдано — получатель подтвердит в своём приложении' : 'Передано подрядчику по акту',
      );
      onClose();
    },
  });
  const valid = f.returnBy && (to === 'person' ? f.userId : f.counterpartyId && f.actNumber.trim());
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Выдать ${u.inventoryNumber}`}
      description="Сотрудник подтверждает получение в своём приложении; подрядчику — по акту с датой возврата (СК-29, СК-33)"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" disabled={!valid} loading={m.isPending} onClick={() => m.mutate()}>
            Выдать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Кому">
          <Select value={to} onChange={(e) => setTo(e.target.value as 'person' | 'contractor')}>
            <option value="person">Сотруднику</option>
            <option value="contractor">Подрядчику по акту</option>
          </Select>
        </Field>
        {to === 'person' ? (
          <Field label="Сотрудник">
            <Select value={f.userId} onChange={(e) => setF({ ...f, userId: e.target.value })}>
              <option value="">Выберите</option>
              {(people.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <>
            <Field label="Подрядчик">
              <Select
                value={f.counterpartyId}
                onChange={(e) => setF({ ...f, counterpartyId: e.target.value })}
              >
                <option value="">Выберите</option>
                {(contractors.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Номер акта">
              <Input value={f.actNumber} onChange={(e) => setF({ ...f, actNumber: e.target.value })} />
            </Field>
          </>
        )}
        <Field label="Вернуть до">
          <Input
            type="date"
            min={todayLocal()}
            value={f.returnBy}
            onChange={(e) => setF({ ...f, returnBy: e.target.value })}
          />
        </Field>
        <Field label="Проект" hint="кому начисляются дни на руках (СК-32); пусто — проект склада">
          <Select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })}>
            <option value="">Проект склада</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Комплект или работа">
          <Input value={f.kitRef} onChange={(e) => setF({ ...f, kitRef: e.target.value })} />
        </Field>
        <Field label="Примечание">
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

function ReturnDialog({ u, onClose, onDone }: { u: ToolUnit; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ condition: u.condition ?? '', note: '', hours: '' });
  const m = useMutation({
    mutationFn: () =>
      api<ToolUnit>(`/warehouse/tools/${u.id}/return`, {
        body: {
          condition: f.condition.trim() || null,
          note: f.note.trim() || null,
          hours: u.resourceKind === 'hours' && f.hours.trim() ? parseNum(f.hours) : null,
        },
      }),
    onSuccess: () => {
      onDone();
      toast('Возвращено на склад');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Возврат ${u.inventoryNumber}`}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>
            Принять на склад
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Состояние при возврате">
          <Input value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} />
        </Field>
        {u.resourceKind === 'hours' ? (
          <Field label="Моточасы за выдачу" hint={`наработка ${u.resourceUsed} из ${u.resourceLimit ?? '—'}`}>
            <Input
              inputMode="decimal"
              value={f.hours}
              onChange={(e) => setF({ ...f, hours: e.target.value })}
            />
          </Field>
        ) : null}
        <Field label="Примечание">
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Направление в ремонт (СК-31): сервис, срок, причина */
function RepairDialog({ u, onClose, onDone }: { u: ToolUnit; onClose: () => void; onDone: () => void }) {
  const contractors = useQuery(counterpartiesQuery('contractor'));
  const [f, setF] = useState({ counterpartyId: '', dueOn: '', reason: '' });
  const m = useMutation({
    mutationFn: () =>
      api<ToolUnit>(`/warehouse/tools/${u.id}/repair`, {
        body: { counterpartyId: f.counterpartyId || null, dueOn: f.dueOn, reason: f.reason.trim() },
      }),
    onSuccess: () => {
      onDone();
      toast('Направлен в ремонт');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`В ремонт: ${u.inventoryNumber}`}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={!f.dueOn || f.reason.trim().length < 3}
            onClick={() => m.mutate()}
          >
            Направить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Сервис" hint="пусто — своими силами">
          <Select value={f.counterpartyId} onChange={(e) => setF({ ...f, counterpartyId: e.target.value })}>
            <option value="">Своими силами</option>
            {(contractors.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Срок ремонта">
          <Input
            type="date"
            min={todayLocal()}
            value={f.dueOn}
            onChange={(e) => setF({ ...f, dueOn: e.target.value })}
          />
        </Field>
        <Field label="Неисправность">
          <Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Закрытие ремонта: исправен или списан, затраты (по уровню денег) */
function RepairCloseDialog({ u, onClose, onDone }: { u: ToolUnit; onClose: () => void; onDone: () => void }) {
  const me = useMe();
  const money = seesMoney(me.access, 'warehouse');
  const [f, setF] = useState({ serviceable: true, result: '', cost: '' });
  const m = useMutation({
    mutationFn: () =>
      api<ToolUnit>(`/warehouse/tools/${u.id}/repair/close`, {
        body: {
          serviceable: f.serviceable,
          result: f.result.trim(),
          cost: money && f.cost.trim() ? parseNum(f.cost) : null,
        },
      }),
    onSuccess: () => {
      onDone();
      toast('Ремонт закрыт');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Закрыть ремонт: ${u.inventoryNumber}`}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={
              f.result.trim().length < 3 || (f.cost.trim() !== '' && !Number.isFinite(parseNum(f.cost)))
            }
            onClick={() => m.mutate()}
          >
            Закрыть ремонт
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Итог">
          <Select
            value={f.serviceable ? 'ok' : 'off'}
            onChange={(e) => setF({ ...f, serviceable: e.target.value === 'ok' })}
          >
            <option value="ok">Исправен — в работу</option>
            <option value="off">Не подлежит ремонту — списать</option>
          </Select>
        </Field>
        <Field label="Что сделано">
          <Textarea rows={2} value={f.result} onChange={(e) => setF({ ...f, result: e.target.value })} />
        </Field>
        <Field label="Затраты без НДС" hint={money ? undefined : 'нужен уровень денег «Склад»'}>
          <Input
            inputMode="decimal"
            disabled={!money}
            value={f.cost}
            onChange={(e) => setF({ ...f, cost: e.target.value })}
          />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Продление ресурса — руководитель проекта (СК-31) */
function ExtendDialog({ u, onClose, onDone }: { u: ToolUnit; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ limit: u.resourceLimit ? String(u.resourceLimit) : '', note: '' });
  const m = useMutation({
    mutationFn: () =>
      api<ToolUnit>(`/warehouse/tools/${u.id}/extend`, {
        body: { resourceLimit: parseNum(f.limit), note: f.note.trim() },
      }),
    onSuccess: () => {
      onDone();
      toast('Ресурс продлён');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Продлить ресурс"
      description={`Наработка ${u.resourceUsed} из ${u.resourceLimit ?? '—'} ${u.resourceKind === 'hours' ? 'моточасов' : 'дней'}`}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={!(parseNum(f.limit) > u.resourceUsed) || f.note.trim().length < 3}
            onClick={() => m.mutate()}
          >
            Продлить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={`Новый ресурс, ${u.resourceKind === 'hours' ? 'моточасов' : 'дней'}`}>
          <Input
            inputMode="decimal"
            value={f.limit}
            onChange={(e) => setF({ ...f, limit: e.target.value })}
          />
        </Field>
        <Field label="Основание">
          <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

function CheckDialog({ u, onClose, onDone }: { u: ToolUnit; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ checkedOn: todayLocal(), checkDocument: '', note: '' });
  const m = useMutation({
    mutationFn: () =>
      api<ToolUnit>(`/warehouse/tools/${u.id}/check`, {
        body: {
          checkedOn: f.checkedOn,
          checkDocument: f.checkDocument.trim() || null,
          note: f.note.trim() || null,
        },
      }),
    onSuccess: () => {
      onDone();
      toast('Проверка записана, следующий срок пересчитан');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={CHECK_KINDS[u.checkKind]}
      description={`Следующая — через ${u.checkIntervalDays} дн.`}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!f.checkedOn} onClick={() => m.mutate()}>
            Записать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Дата проверки">
          <Input
            type="date"
            max={todayLocal()}
            value={f.checkedOn}
            onChange={(e) => setF({ ...f, checkedOn: e.target.value })}
          />
        </Field>
        <Field label="Сертификат, запись в журнале, бирка">
          <Input value={f.checkDocument} onChange={(e) => setF({ ...f, checkDocument: e.target.value })} />
        </Field>
        <Field label="Примечание">
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

function StateDialog({ u, onClose, onDone }: { u: ToolUnit; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ state: u.state as ToolState, note: '' });
  const m = useMutation({
    mutationFn: () =>
      api<ToolUnit>(`/warehouse/tools/${u.id}/state`, { body: { state: f.state, note: f.note.trim() } }),
    onSuccess: () => {
      onDone();
      toast('Состояние записано');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Состояние экземпляра"
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={f.note.trim().length < 3}
            onClick={() => m.mutate()}
          >
            Записать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Состояние">
          <Select value={f.state} onChange={(e) => setF({ ...f, state: e.target.value as ToolState })}>
            {(Object.keys(TOOL_STATES) as ToolState[]).map((k) => (
              <option key={k} value={k}>
                {TOOL_STATES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Причина">
          <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Новый экземпляр или правка карточки */
export function ToolUnitForm({
  initial,
  presetWarehouseId,
  onSaved,
  onCancel,
}: {
  initial?: ToolUnit;
  presetWarehouseId?: string;
  onSaved: (u: ToolUnit) => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const items = useQuery(itemsQuery({ kind: 'tool' }));
  const warehouses = useQuery(warehousesQuery);
  const [f, setF] = useState({
    itemId: initial?.itemId ?? '',
    inventoryNumber: initial?.inventoryNumber ?? '',
    serialNumber: initial?.serialNumber ?? '',
    model: initial?.model ?? '',
    warehouseId: initial?.warehouseId ?? presetWarehouseId ?? '',
    condition: initial?.condition ?? '',
    checkKind: (initial?.checkKind ?? 'none') as CheckKind,
    checkIntervalDays: initial?.checkIntervalDays ? String(initial.checkIntervalDays) : '',
    lastCheckOn: initial?.lastCheckOn ?? '',
    checkDocument: initial?.checkDocument ?? '',
    dailyRate:
      initial?.dailyRate !== null && initial?.dailyRate !== undefined ? String(initial.dailyRate) : '',
    resourceKind: (initial?.resourceKind ?? '') as '' | 'days' | 'hours',
    resourceLimit: initial?.resourceLimit ? String(initial.resourceLimit) : '',
    mechanicUserId: initial?.mechanicUserId ?? '',
    note: initial?.note ?? '',
  });
  const people = useQuery(peopleQuery);
  const me = useMe();
  const money = seesMoney(me.access, 'warehouse');
  const m = useMutation({
    mutationFn: () => {
      const body: ToolUnitInput = {
        ...(money ? { dailyRate: f.dailyRate.trim() ? parseNum(f.dailyRate) : null } : {}),
        resourceKind: f.resourceKind || null,
        resourceLimit: f.resourceKind && f.resourceLimit.trim() ? parseNum(f.resourceLimit) : null,
        itemId: f.itemId,
        inventoryNumber: f.inventoryNumber.trim(),
        serialNumber: f.serialNumber.trim() || null,
        model: f.model.trim() || null,
        warehouseId: f.warehouseId,
        condition: f.condition.trim() || null,
        checkKind: f.checkKind,
        checkIntervalDays: f.checkKind === 'none' ? null : Number(f.checkIntervalDays) || null,
        lastCheckOn: f.lastCheckOn || null,
        checkDocument: f.checkDocument.trim() || null,
        mechanicUserId: f.mechanicUserId || null,
        note: f.note.trim() || null,
      };
      return initial
        ? api<ToolUnit>(`/warehouse/tools/${initial.id}`, { method: 'PATCH', body })
        : api<ToolUnit>('/warehouse/tools', { body });
    },
    onSuccess: (u) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(initial ? 'Карточка сохранена' : `Экземпляр ${u.inventoryNumber} заведён`);
      onSaved(u);
    },
  });
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Позиция каталога"
          hint="только вид «Инструмент и оборудование»"
          className="sm:col-span-2"
        >
          <Select value={f.itemId} onChange={(e) => setF({ ...f, itemId: e.target.value })}>
            <option value="">Выберите</option>
            {(items.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Инвентарный номер">
          <Input
            value={f.inventoryNumber}
            onChange={(e) => setF({ ...f, inventoryNumber: e.target.value })}
          />
        </Field>
        <Field label="Серийный номер">
          <Input value={f.serialNumber} onChange={(e) => setF({ ...f, serialNumber: e.target.value })} />
        </Field>
        <Field label="Модель">
          <Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
        </Field>
        <Field label="Склад">
          <Select value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}>
            <option value="">Выберите</option>
            {(warehouses.data ?? [])
              .filter((w) => w.canOperate && w.kind !== 'transit')
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Вид проверки (СК-30)">
          <Select
            value={f.checkKind}
            onChange={(e) => setF({ ...f, checkKind: e.target.value as CheckKind })}
          >
            {(Object.keys(CHECK_KINDS) as CheckKind[]).map((k) => (
              <option key={k} value={k}>
                {CHECK_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Интервал, дней" hint="поверка — из описания типа СИ">
          <Input
            inputMode="numeric"
            disabled={f.checkKind === 'none'}
            value={f.checkIntervalDays}
            onChange={(e) => setF({ ...f, checkIntervalDays: e.target.value })}
          />
        </Field>
        <Field label="Последняя проверка">
          <Input
            type="date"
            disabled={f.checkKind === 'none'}
            value={f.lastCheckOn}
            onChange={(e) => setF({ ...f, lastCheckOn: e.target.value })}
          />
        </Field>
        <Field label="Документ проверки" hint="сертификат поверки, журнал, бирка">
          <Input
            disabled={f.checkKind === 'none'}
            value={f.checkDocument}
            onChange={(e) => setF({ ...f, checkDocument: e.target.value })}
          />
        </Field>
        <Field label="Состояние" className="sm:col-span-2">
          <Input value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} />
        </Field>
        <Field
          label="Ответственный механик"
          hint="ему ставятся работы по консервации на хранении «механику» (СК-38)"
          className="sm:col-span-2"
        >
          <Select value={f.mechanicUserId} onChange={(e) => setF({ ...f, mechanicUserId: e.target.value })}>
            <option value="">Не назначен — работы кладовщику склада</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="Ставка за день"
          hint={money ? 'в себестоимость проекта по дням на руках (СК-32)' : 'нужен уровень денег «Склад»'}
        >
          <Input
            inputMode="decimal"
            disabled={!money}
            value={f.dailyRate}
            onChange={(e) => setF({ ...f, dailyRate: e.target.value })}
          />
        </Field>
        <Field label="Ресурс считается">
          <Select
            value={f.resourceKind}
            onChange={(e) => setF({ ...f, resourceKind: e.target.value as '' | 'days' | 'hours' })}
          >
            <option value="">Не считается</option>
            <option value="days">В днях на руках</option>
            <option value="hours">В моточасах</option>
          </Select>
        </Field>
        <Field
          label="Ресурс до"
          hint={f.resourceKind === 'hours' ? 'моточасов' : f.resourceKind === 'days' ? 'дней' : undefined}
        >
          <Input
            inputMode="decimal"
            disabled={!f.resourceKind}
            value={f.resourceLimit}
            onChange={(e) => setF({ ...f, resourceLimit: e.target.value })}
          />
        </Field>
      </div>
      <Field label="Примечание">
        <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <ErrorBox error={m.error} />
      <div className="flex gap-2">
        <Button
          variant="primary"
          loading={m.isPending}
          disabled={
            !f.itemId ||
            !f.warehouseId ||
            !f.inventoryNumber.trim() ||
            (f.checkKind !== 'none' && !Number(f.checkIntervalDays)) ||
            (f.dailyRate.trim() !== '' && !Number.isFinite(parseNum(f.dailyRate))) ||
            (f.resourceKind !== '' && !(parseNum(f.resourceLimit) > 0))
          }
          onClick={() => m.mutate()}
        >
          {initial ? 'Сохранить' : 'Завести экземпляр'}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Отмена
          </Button>
        ) : null}
      </div>
    </div>
  );
}
